import React, { useEffect } from 'react';
import { SITE_CONTENT } from '../constants';

// Declare Plyr globally
declare const Plyr: any;

interface MediaSectionProps {
  activeTitle?: string | null;
}

const MediaSection: React.FC<MediaSectionProps> = ({ activeTitle }) => {
  const { media } = SITE_CONTENT;
  const playersRef = React.useRef<Array<{ instance: any; title: string }>>([]);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const normalize = (str: string) => str.toLowerCase().trim().replace(/\s+/g, ' ');

  // Calcolo per bilanciare le colonne (considerando che gli audio sono più bassi dei video)
  // Spostiamo più video a destra per compensare l'altezza minore degli audio
  const leftCount = 12; 
  const leftVideos = media.videos.slice(0, leftCount);
  const rightVideos = media.videos.slice(leftCount);

  useEffect(() => {
    let audioTimeout: any;
    let activeTimeout: any;
    let observer: IntersectionObserver | null = null;

    const initPlayers = () => {
      if (typeof Plyr === 'undefined') return false;
      if (!containerRef.current) return false;
      
      playersRef.current = [];
      
      const videoElements = containerRef.current.querySelectorAll('.bottega-video-player');
      const audioElements = containerRef.current.querySelectorAll('.bottega-audio-player');
      
      const videoConfig = {
        controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen'],
        tooltips: { controls: true, seek: true },
        origin: window.location.origin,
        youtube: { noCookie: true, rel: 0, modestbranding: 1, origin: window.location.origin }
      };

      const audioConfig = {
        tooltips: { controls: true, seek: true },
        origin: window.location.origin
      };

      // Inizializzazione Audio
      audioTimeout = setTimeout(() => {
        audioElements.forEach((el) => {
          try {
            if (el.closest('.plyr')) return; // Controllo più generico
            const title = el.getAttribute('data-title') || "";
            const player = new Plyr(el, audioConfig);
            player.on('play', () => {
              playersRef.current.forEach(p => { if (p.instance !== player) p.instance.pause(); });
            });
            playersRef.current.push({ instance: player, title });
          } catch (err) { console.error('Audio Plyr Error:', err); }
        });
      }, 500);

      // Inizializzazione Video (Lazy)
      observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement;
            const isVideo = el.classList.contains('bottega-video-player');
            const title = el.getAttribute('data-title') || "";

            if (isVideo) {
              try {
                if (el.closest('.plyr--video')) return;
                const videoEl = el as HTMLVideoElement;
                videoEl.preload = 'metadata'; 
                const player = new Plyr(el, videoConfig);
                player.on('play', () => {
                  playersRef.current.forEach(p => { if (p.instance !== player) p.instance.pause(); });
                });
                playersRef.current.push({ instance: player, title });
              } catch (err) { console.error('Video Plyr Lazy Init Error:', err); }
            }
            observer?.unobserve(el);
          }
        });
      }, { rootMargin: '400px' });

      videoElements.forEach(el => observer?.observe(el));

      if (activeTitle) {
        activeTimeout = setTimeout(() => {
          const target = playersRef.current.find(p => normalize(p.title) === normalize(activeTitle));
          if (target) {
            target.instance.play();
            const el = target.instance.elements.container;
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 1200);
      }

      return true;
    };

    if (!initPlayers()) {
      const interval = setInterval(() => {
        if (initPlayers()) clearInterval(interval);
      }, 500);
      return () => clearInterval(interval);
    }

    return () => {
      clearTimeout(audioTimeout);
      clearTimeout(activeTimeout);
      observer?.disconnect();
      playersRef.current.forEach(p => {
        if (p.instance && typeof p.instance.destroy === 'function') {
          try { p.instance.destroy(); } catch (e) {}
        }
      });
      playersRef.current = [];
    };
  }, [activeTitle]);

  const renderVideo = (video: any, idx: number) => (
    <div key={idx} className="group relative p-1 bg-midnight border border-gold/10 shadow-xl transition-all duration-500 hover:border-gold/30">
      {/* Decorative Corners */}
      <div className="absolute top-1 left-1 w-4 h-4 border-t border-l border-gold/60 group-hover:w-6 group-hover:h-6 transition-all duration-500 z-20 pointer-events-none" />
      <div className="absolute bottom-1 right-1 w-4 h-4 border-b border-r border-gold/60 group-hover:w-6 group-hover:h-6 transition-all duration-500 z-20 pointer-events-none" />
      
      <div className="relative z-10">
        {/* Titolo sopra il video */}
        <div className="p-5 flex items-center justify-between bg-white/5">
          <div className="flex items-center space-x-4">
            <div className="w-10 h-10 flex items-center justify-center border border-gold/30 rounded-full text-gold group-hover:border-gold group-hover:bg-gold/10 transition-all duration-500">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h4 className="text-white font-serif text-base group-hover:text-gold transition-colors">{video.title}</h4>
              <span className="text-[10px] text-gold/60 uppercase tracking-widest">Video Performance</span>
            </div>
          </div>
        </div>

        <div className="aspect-video bg-black relative border-t border-white/5 group/player overflow-hidden">
          {video.type === 'youtube' ? (
            <div className="bottega-video-player" data-plyr-provider="youtube" data-plyr-embed-id={video.id} data-title={video.title}></div>
          ) : (
            <video 
              className="bottega-video-player" 
              playsInline 
              controls 
              preload="none" 
              data-title={video.title}
            >
              <source src={video.url} type="video/mp4" />
            </video>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-6" ref={containerRef}>
      <div className="relative mb-32 max-w-4xl mx-auto">
        {/* Cornice esterna decorativa */}
        <div className="absolute -inset-4 border border-gold/20 pointer-events-none" />
        
        <div className="relative rounded-sm overflow-hidden border border-gold/20 shadow-2xl group">
          <div className="absolute inset-0 bg-midnight/60 z-10 group-hover:bg-midnight/40 transition-colors duration-700" />
          <img 
            src="https://bottegadellevoci.it/Foto/Microfono.jpg" 
            alt="L'anima del canto" 
            className="w-full h-[200px] md:h-[300px] object-cover object-center grayscale group-hover:grayscale-0 transition-all duration-1000 scale-105 group-hover:scale-100"
            referrerPolicy="no-referrer"
          />
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center p-4">
            <span className="text-gold font-sans uppercase tracking-[0.6em] text-xs md:text-sm font-medium mb-2 animate-fadeIn">Emozioni in voce</span>
            <h2 className="text-3xl md:text-5xl font-serif text-white mb-4 drop-shadow-lg">Video & MP3</h2>
            <div className="w-16 h-px bg-gold" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 relative">
        {/* Divisore verticale per desktop */}
        <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-gold/20 -translate-x-1/2" />

        {/* Colonna Sinistra */}
        <div className="space-y-12">
          <div className="flex items-center space-x-4 mb-8">
            <h3 className="text-2xl font-serif text-gold uppercase tracking-widest">Esecuzioni Video</h3>
            <div className="flex-grow h-px bg-gold/10" />
          </div>
          
          <div className="space-y-10">
            {leftVideos.map((video, idx) => renderVideo(video, idx))}
          </div>
        </div>

        {/* Colonna Destra */}
        <div className="space-y-12">
          {/* Sezione Audio */}
          <div className="flex items-center space-x-4 mb-8">
            <h3 className="text-2xl font-serif text-gold uppercase tracking-widest">Registrazioni Audio</h3>
            <div className="flex-grow h-px bg-gold/10" />
          </div>
          
          <div className="space-y-10">
            {media.audios.map((audio, idx) => (
              <div key={audio.url} className="group relative p-1 bg-midnight border border-gold/10 shadow-xl transition-all duration-500 hover:border-gold/30">
                {/* Decorative Corners */}
                <div className="absolute top-1 left-1 w-4 h-4 border-t border-l border-gold/60 group-hover:w-6 group-hover:h-6 transition-all duration-500 z-20 pointer-events-none" />
                <div className="absolute bottom-1 right-1 w-4 h-4 border-b border-r border-gold/60 group-hover:w-6 group-hover:h-6 transition-all duration-500 z-20 pointer-events-none" />
                
                <div className="relative z-10 bg-midnight/60 p-5 backdrop-blur-sm">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-10 h-10 flex items-center justify-center border border-gold/30 rounded-full text-gold group-hover:border-gold group-hover:bg-gold/10 transition-all duration-500">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-white font-serif text-base">{audio.title}</h4>
                      <span className="text-[10px] text-gold/60 uppercase tracking-widest">MP3 Recording</span>
                    </div>
                  </div>
                  <div className="plyr-audio-container">
                    <audio className="bottega-audio-player" data-title={audio.title} controls>
                      <source src={audio.url} type="audio/mp3" />
                    </audio>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Video rimanenti per bilanciare la colonna */}
          {rightVideos.length > 0 && (
            <div className="pt-12 space-y-10">
              <div className="flex items-center space-x-4 mb-8">
                <h3 className="text-xl font-serif text-gold/60 uppercase tracking-widest">Altre Esecuzioni Video</h3>
                <div className="flex-grow h-px bg-gold/5" />
              </div>
              {rightVideos.map((video, idx) => renderVideo(video, idx + leftVideos.length))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaSection;
